import { useState, useEffect, useMemo } from 'react';
import { documentsAPI, clientsAPI, inventoryAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, FileText, Download, Trash2, Eye, Search, Filter, X, MoreVertical, Send, ArrowUpDown } from 'lucide-react';
import { BulkSelectCheckbox, BulkActionBar } from '@/components/BulkActions';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TAX_PRESETS = ['HST', 'GST', 'Tax', 'No Tax'];

const emptyForm = {
  type: 'quote', client_id: '', inventory_id: '', deal_id: '',
  items: [{ description: '', quantity: '1', amount: '' }],
  notes: '', deposit_amount: '', deposit_instructions: '',
  tax_label: 'HST', tax_rate: '13',
};

export default function AdminDocuments() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [docs, setDocs] = useState([]);
  const [clients, setClients] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [clientSearch, setClientSearch] = useState('');
  const [vehicleSearch, setVehicleSearch] = useState('');

  // Search and filters
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');
  const [dateRange, setDateRange] = useState('all'); // all, today, 7d, 30d, custom
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [selected, setSelected] = useState([]);
  const [resending, setResending] = useState(null);
  const toggleSelect = (id) => setSelected(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const load = async () => {
    setLoading(true);
    try {
      const [d, c, inv] = await Promise.all([documentsAPI.list(), clientsAPI.list(), inventoryAPI.list({ limit: 100 })]);
      setDocs(d.data);
      setClients(c.data);
      setInventory(inv.data.items || []);
    } catch (e) { console.error('Documents load error:', e); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { description: '', quantity: '1', amount: '' }] }));
  const updateItem = (idx, key, val) => {
    setForm(f => {
      const items = [...f.items];
      items[idx] = { ...items[idx], [key]: val };
      return { ...f, items };
    });
  };
  const removeItem = (idx) => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  // Live totals from form (single source of truth for the modal)
  const totals = useMemo(() => {
    const subtotal = form.items.reduce((sum, it) => {
      const q = Math.max(1, parseInt(it.quantity, 10) || 1);
      const a = parseFloat(it.amount) || 0;
      return sum + q * a;
    }, 0);
    const rate = parseFloat(form.tax_rate) || 0;
    const tax = form.tax_label === 'No Tax' ? 0 : subtotal * (rate / 100);
    return { subtotal, tax, total: subtotal + tax };
  }, [form.items, form.tax_rate, form.tax_label]);

  const validateForm = () => {
    if (!form.client_id) return 'Please select a client.';
    if (form.type === 'quote' || form.type === 'invoice') {
      const valid = form.items.filter(it => (it.description || '').trim() && (parseFloat(it.amount) || 0) > 0);
      if (valid.length === 0) return 'Please add at least one line item with a description and amount.';
      for (const it of form.items) {
        if (!it.description?.trim()) continue;
        const q = parseInt(it.quantity, 10);
        if (it.quantity !== '' && (isNaN(q) || q < 1)) return 'Quantity must be a whole number ≥ 1.';
        const a = parseFloat(it.amount);
        if (isNaN(a) || a < 0) return 'Amount must be a valid non-negative number.';
      }
      const r = parseFloat(form.tax_rate);
      if (form.tax_label !== 'No Tax' && (isNaN(r) || r < 0)) return 'Tax % must be a valid non-negative number.';
    }
    if (form.type === 'hold_deposit') {
      const amt = parseFloat(form.deposit_amount);
      if (isNaN(amt) || amt <= 0) return 'Please enter a valid deposit amount.';
    }
    return null;
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    const err = validateForm();
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      const cleanItems = form.items
        .filter(it => (it.description || '').trim() || parseFloat(it.amount) > 0)
        .map(it => ({
          description: it.description?.trim() || '',
          quantity: Math.max(1, parseInt(it.quantity, 10) || 1),
          amount: parseFloat(it.amount) || 0,
        }));
      const payload = {
        type: form.type,
        client_id: form.client_id || null,
        inventory_id: form.inventory_id || null,
        deal_id: form.deal_id || null,
        items: cleanItems,
        notes: form.notes || null,
        subtotal: totals.subtotal || null,
        tax: totals.tax || null,
        total: totals.total || null,
        tax_label: form.tax_label,
        tax_rate: form.tax_label === 'No Tax' ? 0 : (parseFloat(form.tax_rate) || 0),
        deposit_amount: parseFloat(form.deposit_amount) || null,
        deposit_instructions: form.deposit_instructions || null,
      };
      const res = await documentsAPI.generate(payload);
      toast.success(`${form.type.replace('_', ' ')} generated and saved to Document Center`);
      setDialogOpen(false);
      setForm(emptyForm);
      setClientSearch('');
      setVehicleSearch('');
      load();
      if (res.data.url) {
        window.open(`${API_URL}${res.data.url}`, '_blank');
      }
    } catch { toast.error('Generation failed — please try again. The document was NOT saved.'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this document?')) return;
    try { await documentsAPI.delete(id); toast.success('Deleted'); load(); } catch { toast.error('Failed'); }
  };

  const handleResend = async (doc) => {
    setResending(doc.id);
    try {
      const res = await documentsAPI.resend(doc.id);
      toast.success(res.data.message || 'Document resent!');
    } catch { toast.error('Failed to resend'); }
    setResending(null);
  };

  const handleView = (doc) => {
    if (doc.url) {
      window.open(`${API_URL}${doc.url}`, '_blank');
    } else {
      toast.error('No file available for this document');
    }
  };

  const handleDownload = async (doc) => {
    if (doc.url) {
      try {
        const response = await fetch(`${API_URL}${doc.url}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.filename || 'document.pdf';
        a.click();
        window.URL.revokeObjectURL(url);
      } catch { toast.error('Download failed'); }
    } else {
      toast.error('No file available');
    }
  };

  const getClientName = (id) => clients.find(c => c.id === id)?.name || '--';
  const getClientCompany = (id) => {
    const c = clients.find(c => c.id === id);
    return c?.company || c?.name || '--';
  };

  // Date range helpers
  const dateInRange = (iso) => {
    if (dateRange === 'all') return true;
    if (!iso) return false;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return false;
    const now = new Date();
    if (dateRange === 'today') {
      return d.toDateString() === now.toDateString();
    }
    if (dateRange === '7d') {
      const cutoff = new Date(now); cutoff.setDate(cutoff.getDate() - 7);
      return d >= cutoff;
    }
    if (dateRange === '30d') {
      const cutoff = new Date(now); cutoff.setDate(cutoff.getDate() - 30);
      return d >= cutoff;
    }
    if (dateRange === 'custom') {
      if (customFrom) {
        const from = new Date(customFrom + 'T00:00:00');
        if (d < from) return false;
      }
      if (customTo) {
        const to = new Date(customTo + 'T23:59:59.999');
        if (d > to) return false;
      }
      return true;
    }
    return true;
  };

  const filteredDocs = useMemo(() => {
    const filtered = docs.filter(doc => {
      if (typeFilter !== 'all' && doc.type !== typeFilter) return false;
      if (clientFilter !== 'all' && doc.client_id !== clientFilter) return false;
      if (!dateInRange(doc.created_at)) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const clientName = getClientName(doc.client_id).toLowerCase();
        const clientCompany = getClientCompany(doc.client_id).toLowerCase();
        const hay = [
          doc.filename, doc.type, doc.unit_title, doc.stock_number,
          doc.invoice_number, doc.quote_number, doc.reference_number,
          doc.recipient_name, doc.recipient_email, doc.created_by_name,
          clientName, clientCompany,
          doc.total != null ? String(doc.total) : '',
        ].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    // Sort
    const sorters = {
      newest: (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0),
      oldest: (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0),
      client_asc: (a, b) => getClientCompany(a.client_id).localeCompare(getClientCompany(b.client_id)),
      client_desc: (a, b) => getClientCompany(b.client_id).localeCompare(getClientCompany(a.client_id)),
      amount_desc: (a, b) => (Number(b.total) || 0) - (Number(a.total) || 0),
      amount_asc: (a, b) => (Number(a.total) || 0) - (Number(b.total) || 0),
      type: (a, b) => (a.type || '').localeCompare(b.type || ''),
    };
    return [...filtered].sort(sorters[sortBy] || sorters.newest);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docs, typeFilter, clientFilter, searchQuery, clients, dateRange, customFrom, customTo, sortBy]);

  const docTypes = useMemo(() => [...new Set(docs.map(d => d.type).filter(Boolean))], [docs]);

  return (
    <div data-testid="admin-documents">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold text-gray-900 uppercase">{isAdmin ? 'Document Center' : 'My Documents'}</h1>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">{filteredDocs.length} of {docs.length} docs</span>
          <Button className="bg-[#E31937] text-white hover:bg-[#C4122C]" onClick={() => setDialogOpen(true)} data-testid="create-doc-btn">
            <Plus className="w-4 h-4 mr-1" /> Create Document
          </Button>
        </div>
      </div>

      {/* Search & Filter Bar */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search filename, client, type, VIN, stock #, quote/invoice #..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-9 h-9 text-sm"
              data-testid="doc-search-input"
            />
          </div>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-[150px] h-9 text-sm" data-testid="doc-type-filter">
              <Filter className="w-3 h-3 mr-1.5 text-gray-400" /><SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {docTypes.map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, ' ')}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={clientFilter} onValueChange={setClientFilter}>
            <SelectTrigger className="w-[160px] h-9 text-sm" data-testid="doc-client-filter">
              <SelectValue placeholder="Client" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Clients</SelectItem>
              {[...new Set(docs.map(d => d.client_id).filter(Boolean))].map(cid => (
                <SelectItem key={cid} value={cid}>{getClientCompany(cid)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-[140px] h-9 text-sm" data-testid="doc-date-filter">
              <SelectValue placeholder="Date" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Dates</SelectItem>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="custom">Custom range...</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[170px] h-9 text-sm" data-testid="doc-sort">
              <ArrowUpDown className="w-3 h-3 mr-1.5 text-gray-400" /><SelectValue placeholder="Sort" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest first</SelectItem>
              <SelectItem value="oldest">Oldest first</SelectItem>
              <SelectItem value="client_asc">Client A → Z</SelectItem>
              <SelectItem value="client_desc">Client Z → A</SelectItem>
              <SelectItem value="amount_desc">Amount: High → Low</SelectItem>
              <SelectItem value="amount_asc">Amount: Low → High</SelectItem>
              <SelectItem value="type">Document Type</SelectItem>
            </SelectContent>
          </Select>
          {(searchQuery || typeFilter !== 'all' || clientFilter !== 'all' || dateRange !== 'all' || sortBy !== 'newest') && (
            <Button variant="ghost" size="sm" onClick={() => { setSearchQuery(''); setTypeFilter('all'); setClientFilter('all'); setDateRange('all'); setCustomFrom(''); setCustomTo(''); setSortBy('newest'); }} className="h-9 text-xs text-gray-500" data-testid="doc-clear-filters">
              <X className="w-3 h-3 mr-1" /> Clear
            </Button>
          )}
        </div>
        {dateRange === 'custom' && (
          <div className="flex flex-wrap gap-2 items-center mt-3 pt-3 border-t border-gray-100">
            <label className="text-xs text-gray-500">From</label>
            <Input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} className="h-8 w-[160px] text-sm" data-testid="doc-date-from" />
            <label className="text-xs text-gray-500 ml-2">To</label>
            <Input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} className="h-8 w-[160px] text-sm" data-testid="doc-date-to" />
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-gray-200 hover:bg-transparent">
              <TableHead className="w-10"><BulkSelectCheckbox checked={selected.length === filteredDocs.length && filteredDocs.length > 0} onCheckedChange={() => setSelected(p => p.length === filteredDocs.length ? [] : filteredDocs.map(d => d.id))} testId="select-all-docs" /></TableHead>
              <TableHead className="text-gray-500">Client / Company</TableHead>
              <TableHead className="text-gray-500">Type</TableHead>
              <TableHead className="text-gray-500 hidden md:table-cell">Total</TableHead>
              <TableHead className="text-gray-500 hidden lg:table-cell">Created By</TableHead>
              <TableHead className="text-gray-500 hidden sm:table-cell">Date</TableHead>
              <TableHead className="text-gray-500 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="text-center text-gray-400 py-8">Loading...</TableCell></TableRow>
            ) : filteredDocs.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center text-gray-400 py-8" data-testid="doc-empty-state">
                {docs.length === 0
                  ? 'No documents generated yet.'
                  : 'No matching documents found. Clear filters to see all documents.'}
              </TableCell></TableRow>
            ) : filteredDocs.map(doc => (
              <TableRow key={doc.id} className="border-gray-200 text-gray-700">
                <TableCell><BulkSelectCheckbox checked={selected.includes(doc.id)} onCheckedChange={() => toggleSelect(doc.id)} testId={`select-doc-${doc.id}`} /></TableCell>
                <TableCell>
                  <div>
                    <p className="text-sm font-medium">{getClientCompany(doc.client_id)}</p>
                    <p className="text-[11px] text-gray-400">{getClientName(doc.client_id) !== getClientCompany(doc.client_id) ? getClientName(doc.client_id) : ''}</p>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge className={
                    doc.type === 'quote' ? 'bg-blue-100 text-blue-800 border border-blue-300' : 
                    doc.type === 'invoice' ? 'bg-green-100 text-green-800 border border-green-300' : 
                    doc.type === 'hold_deposit' ? 'bg-amber-100 text-amber-800 border border-amber-300' :
                    doc.type === 'bill_of_sale' ? 'bg-purple-100 text-purple-800 border border-purple-300' :
                    doc.type === 'signed_document' ? 'bg-indigo-100 text-indigo-800 border border-indigo-300' :
                    'bg-gray-100 text-gray-800 border border-gray-300'
                  }>
                    {doc.type.replace('_', ' ')}
                  </Badge>
                </TableCell>
                <TableCell className="hidden md:table-cell font-medium">{doc.total ? `$${Number(doc.total).toLocaleString()}` : '--'}</TableCell>
                <TableCell className="text-xs hidden lg:table-cell">{doc.created_by_name || '--'}</TableCell>
                <TableCell className="text-xs text-gray-400 hidden sm:table-cell">{new Date(doc.created_at).toLocaleDateString()}</TableCell>
                <TableCell className="text-right">
                  {/* Desktop actions */}
                  <div className="hidden sm:flex gap-1 justify-end">
                    <Button variant="ghost" size="sm" onClick={() => window.open(`${API_URL}${doc.url}`, '_blank')} data-testid={`view-doc-${doc.id}`}>
                      <Eye className="w-3 h-3" />
                    </Button>
                    <Button variant="ghost" size="sm" asChild>
                      <a href={`${API_URL}${doc.url}`} download><Download className="w-3 h-3" /></a>
                    </Button>
                    {isAdmin && (
                      <Button variant="ghost" size="sm" className="text-red-400" onClick={() => handleDelete(doc.id)}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                  {/* Mobile dropdown */}
                  <div className="sm:hidden">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0"><MoreVertical className="w-4 h-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-white">
                        <DropdownMenuItem onClick={() => handleView(doc)} data-testid={`doc-view-${doc.id}`}><Eye className="w-3.5 h-3.5 mr-2" />View</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDownload(doc)} data-testid={`doc-download-${doc.id}`}><Download className="w-3.5 h-3.5 mr-2" />Download</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleResend(doc)} disabled={resending === doc.id} data-testid={`doc-resend-${doc.id}`}>
                          <Send className="w-3.5 h-3.5 mr-2" />{resending === doc.id ? 'Sending...' : 'Resend'}
                        </DropdownMenuItem>
                        {isAdmin && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleDelete(doc.id)} className="text-red-500" data-testid={`doc-delete-${doc.id}`}><Trash2 className="w-3.5 h-3.5 mr-2" />Delete</DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </div>
      </div>
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto bg-white border-gray-200 text-gray-900" data-testid="doc-dialog">
          <DialogHeader>
            <DialogTitle className="text-gray-900 uppercase flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#E31937]" /> Generate Document
            </DialogTitle>
            <DialogDescription className="text-xs text-gray-500">
              Create a Quote, Invoice, or Hold Deposit Instructions. The generated PDF will be saved to the Document Center.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleGenerate} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-500 uppercase">Document Type</label>
                <Select value={form.type} onValueChange={v => setForm(f => ({ ...f, type: v }))}>
                  <SelectTrigger className="bg-gray-100 border-gray-200 mt-1" data-testid="doc-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="quote">Quote</SelectItem>
                    <SelectItem value="invoice">Invoice</SelectItem>
                    <SelectItem value="hold_deposit">Hold Deposit Instructions</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase">Client *</label>
                <div className="mb-1 mt-1">
                  <Input
                    placeholder="Search clients by name, company or email..."
                    value={clientSearch}
                    onChange={e => setClientSearch(e.target.value)}
                    className="bg-gray-50 border-gray-200 h-8 text-xs"
                    data-testid="doc-client-search"
                  />
                </div>
                <Select value={form.client_id || ''} onValueChange={v => setForm(f => ({ ...f, client_id: v }))}>
                  <SelectTrigger className="bg-gray-100 border-gray-200" data-testid="doc-client-select"><SelectValue placeholder="Select Client" /></SelectTrigger>
                  <SelectContent className="max-h-[280px]">
                    {(() => {
                      const q = clientSearch.trim().toLowerCase();
                      const filtered = q
                        ? clients.filter(c => `${c.name || ''} ${c.company || ''} ${c.email || ''}`.toLowerCase().includes(q))
                        : clients;
                      if (filtered.length === 0) {
                        return <div className="px-3 py-2 text-xs text-gray-400">{clients.length === 0 ? 'No clients yet. Add a client first.' : 'No clients match your search.'}</div>;
                      }
                      return filtered.slice(0, 200).map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name}{c.company ? ` — ${c.company}` : ''}</SelectItem>
                      ));
                    })()}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="text-xs text-gray-500 uppercase">Vehicle (Optional)</label>
              <div className="mb-1 mt-1">
                <Input
                  placeholder="Search vehicles by title, stock # or VIN..."
                  value={vehicleSearch}
                  onChange={e => setVehicleSearch(e.target.value)}
                  className="bg-gray-50 border-gray-200 h-8 text-xs"
                  data-testid="doc-vehicle-search"
                />
              </div>
              <Select value={form.inventory_id || 'none'} onValueChange={v => {
                const value = v === 'none' ? '' : v;
                const unit = inventory.find(i => i.id === value);
                setForm(f => {
                  const updated = { ...f, inventory_id: value };
                  if (unit && (f.type === 'quote' || f.type === 'invoice')) {
                    updated.items = [{ description: unit.title || `${unit.year} ${unit.make} ${unit.model}`, quantity: '1', amount: unit.price ? String(unit.price) : '' }];
                  } else if (unit && f.type === 'hold_deposit') {
                    updated.deposit_amount = unit.price ? String(Math.round(unit.price * 0.1)) : '';
                  }
                  return updated;
                });
              }}>
                <SelectTrigger className="bg-gray-100 border-gray-200" data-testid="doc-vehicle-select"><SelectValue placeholder="Select Vehicle (optional)" /></SelectTrigger>
                <SelectContent className="max-h-[280px]">
                  <SelectItem value="none">-- No vehicle --</SelectItem>
                  {(() => {
                    const q = vehicleSearch.trim().toLowerCase();
                    const filtered = q
                      ? inventory.filter(i => {
                          const hay = `${i.title || ''} ${i.year || ''} ${i.make || ''} ${i.model || ''} ${i.stock_number || ''} ${i.vin || ''}`.toLowerCase();
                          return hay.includes(q);
                        })
                      : inventory;
                    return filtered.slice(0, 100).map(i => (
                      <SelectItem key={i.id} value={i.id}>{i.title || `${i.year} ${i.make} ${i.model}`} ({i.stock_number || 'no stock #'})</SelectItem>
                    ));
                  })()}
                </SelectContent>
              </Select>
            </div>

            {(form.type === 'quote' || form.type === 'invoice') && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs text-gray-500 uppercase">Line Items</label>
                  <Button type="button" variant="ghost" size="sm" className="text-[#E31937] text-xs" onClick={addItem} data-testid="doc-add-line-item">+ Add Item</Button>
                </div>
                <div className="hidden sm:grid grid-cols-[1fr_70px_120px_24px] gap-2 mb-1 px-1 text-[10px] uppercase text-gray-400">
                  <span>Description</span>
                  <span className="text-center">Qty</span>
                  <span className="text-right pr-1">Unit Price ($)</span>
                  <span></span>
                </div>
                {form.items.map((item, idx) => (
                  <div key={`item-${idx}`} className="grid grid-cols-[1fr_70px_120px_24px] gap-2 mb-2">
                    <Input placeholder="Description" value={item.description} onChange={e => updateItem(idx, 'description', e.target.value)} className="bg-gray-100 border-gray-200 text-sm" data-testid={`doc-item-desc-${idx}`} />
                    <Input placeholder="1" type="number" min="1" step="1" value={item.quantity} onChange={e => updateItem(idx, 'quantity', e.target.value)} className="bg-gray-100 border-gray-200 text-sm text-center" data-testid={`doc-item-qty-${idx}`} />
                    <Input placeholder="0.00" type="number" min="0" step="0.01" value={item.amount} onChange={e => updateItem(idx, 'amount', e.target.value)} className="bg-gray-100 border-gray-200 text-sm text-right" data-testid={`doc-item-amount-${idx}`} />
                    <button type="button" disabled={form.items.length <= 1} onClick={() => removeItem(idx)} className="text-gray-400 hover:text-red-500 disabled:opacity-30 disabled:cursor-not-allowed" data-testid={`doc-item-remove-${idx}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                {/* Tax controls */}
                <div className="grid grid-cols-[1fr_120px_90px] gap-2 mt-3 pt-3 border-t border-gray-100 items-center">
                  <label className="text-xs text-gray-500 uppercase">Tax</label>
                  <Select value={form.tax_label} onValueChange={v => setForm(f => ({ ...f, tax_label: v, tax_rate: v === 'No Tax' ? '0' : (f.tax_rate || '13') }))}>
                    <SelectTrigger className="bg-gray-100 border-gray-200 h-9 text-sm" data-testid="doc-tax-label"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TAX_PRESETS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <div className="relative">
                    <Input type="number" min="0" step="0.1" value={form.tax_rate} disabled={form.tax_label === 'No Tax'} onChange={e => setForm(f => ({ ...f, tax_rate: e.target.value }))} className="bg-gray-100 border-gray-200 h-9 text-sm text-right pr-6" data-testid="doc-tax-rate" />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">%</span>
                  </div>
                </div>
                <div className="flex justify-end gap-6 text-sm mt-3 pt-2 border-t border-gray-200" data-testid="doc-totals">
                  <span className="text-gray-500">Subtotal: <span className="text-gray-900">${totals.subtotal.toFixed(2)}</span></span>
                  <span className="text-gray-500">{form.tax_label} {form.tax_label !== 'No Tax' ? `${form.tax_rate || 0}%` : ''}: <span className="text-gray-900">${totals.tax.toFixed(2)}</span></span>
                  <span className="text-gray-500 font-bold">Total: <span className="text-[#E31937]">${totals.total.toFixed(2)}</span></span>
                </div>
              </div>
            )}

            {form.type === 'hold_deposit' && (
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-gray-500 uppercase">Deposit Amount ($)</label>
                  <Input type="number" value={form.deposit_amount} onChange={e => setForm(f => ({ ...f, deposit_amount: e.target.value }))} className="bg-gray-100 border-gray-200 mt-1" data-testid="deposit-amount" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase">Custom Instructions (optional)</label>
                  <Textarea value={form.deposit_instructions} onChange={e => setForm(f => ({ ...f, deposit_instructions: e.target.value }))} className="bg-gray-100 border-gray-200 mt-1" rows={4} placeholder="Leave blank for default e-transfer instructions" />
                </div>
              </div>
            )}

            <div>
              <label className="text-xs text-gray-500 uppercase">Notes</label>
              <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="bg-gray-100 border-gray-200 mt-1" rows={2} />
            </div>

            <div className="flex gap-2 justify-end pt-2">
              <Button type="button" variant="outline" className="border-gray-300" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#E31937] text-white hover:bg-[#C4122C]" disabled={saving} data-testid="generate-doc-btn">
                {saving ? 'Generating...' : 'Generate PDF'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <BulkActionBar count={selected.length} onDelete={async () => {
        if (!window.confirm(`Delete ${selected.length} document(s)?`)) return;
        try { await documentsAPI.bulkDelete(selected); toast.success(`${selected.length} document(s) deleted`); setSelected([]); load(); } catch { toast.error('Failed'); }
      }} onClear={() => setSelected([])} label="document(s)" />
    </div>
  );
}
